/**
 * Timeline Dividers Utility
 *
 * Handles the logic for adding date-based dividers to timeline events.
 * Creates a hierarchical timeline with:
 * - Month dividers (e.g., "OCTOBER 2024") for older events
 * - Day dividers (e.g., "Today", "Yesterday", "Monday (Oct 7)", "Oct 2")
 */

/**
 * Get the divider label for a given timestamp
 * @param {Date|string} timestamp - The timestamp to get label for
 * @returns {Object} - { type: 'day' | 'date', label: string }
 */
export function getDividerLabel(timestamp) {
  const now = new Date();
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  const startOfThisWeek = new Date(startOfToday);
  startOfThisWeek.setDate(startOfThisWeek.getDate() - now.getDay()); // Sunday

  // Today
  if (date >= startOfToday) {
    return { type: 'day', label: 'Today' };
  }

  // Yesterday
  if (date >= startOfYesterday) {
    return { type: 'day', label: 'Yesterday' };
  }

  // This week: "Monday (Oct 7)"
  if (date >= startOfThisWeek) {
    return {
      type: 'day',
      label: date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric'
      })
    };
  }

  // Older: just "Oct 7" (date only)
  return {
    type: 'date',
    label: date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    })
  };
}

/**
 * Get the month label for a given timestamp
 * @param {Date|string} timestamp - The timestamp to get month label for
 * @returns {string} - e.g., "October 2024"
 */
export function getMonthLabel(timestamp) {
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric'
  });
}

/**
 * Add timeline dividers to an array of events
 * @param {Array} events - Array of events with timestamp property
 * @returns {Array} - Events with divider objects interspersed
 */
export function addTimelineDividers(events) {
  const grouped = [];
  let lastDayLabel = null;
  let lastMonth = null;

  events.forEach((event, index) => {
    const dividerInfo = getDividerLabel(event.timestamp);
    const monthLabel = getMonthLabel(event.timestamp);

    // Add month divider when month changes (for older dates beyond current week)
    const now = new Date();
    const startOfThisWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    startOfThisWeek.setDate(startOfThisWeek.getDate() - now.getDay());

    if (event.timestamp < startOfThisWeek && monthLabel !== lastMonth) {
      grouped.push({
        type: 'month-divider',
        id: `month-${index}`,
        label: monthLabel
      });
      lastMonth = monthLabel;
    }

    // Add day divider when day changes
    if (dividerInfo.label !== lastDayLabel) {
      grouped.push({
        type: 'day-divider',
        id: `day-${index}`,
        label: dividerInfo.label,
        dividerType: dividerInfo.type
      });
      lastDayLabel = dividerInfo.label;
    }

    grouped.push(event);
  });

  return grouped;
}
